import { useState, useEffect, useCallback, useRef } from 'react';
import inkquoteApi from '../services/inkquoteApi';

/**
 * Hook to manage API sync status and operations
 * Only active in dev mode
 */
export function useApiSync() {
  const [isConnected, setIsConnected] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [queuedCount, setQueuedCount] = useState(0);
  const [error, setError] = useState(null);

  const checkIntervalRef = useRef(null);
  const isDevMode = process.env.REACT_APP_MODE === 'development';

  // Check connection status
  const checkConnection = useCallback(async () => {
    if (!isDevMode || !inkquoteApi.isEnabled()) {
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const result = await inkquoteApi.checkConnection();
      setIsConnected(result.connected);
      setLastCheckTime(new Date());

      if (!result.connected) {
        setError(result.reason);
      }

      // Update queued count
      const queued = inkquoteApi.getQueuedCount();
      setQueuedCount(queued);
    } catch (err) {
      setIsConnected(false);
      setError(err.message);
    } finally {
      setIsChecking(false);
    }
  }, [isDevMode]);

  // Manual sync trigger
  const syncNow = useCallback(async () => {
    if (!isDevMode || !inkquoteApi.isEnabled()) {
      return { success: false, error: 'Dev mode not enabled' };
    }

    setIsSyncing(true);
    setError(null);

    try {
      const result = await inkquoteApi.syncNow();

      if (result.success) {
        setQueuedCount(0);
        // Refresh connection status
        await checkConnection();
      } else {
        setError(result.error);
      }

      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsSyncing(false);
    }
  }, [isDevMode, checkConnection]);

  // Trigger export and reload
  const triggerExport = useCallback(async () => {
    if (!isDevMode || !inkquoteApi.isEnabled()) {
      return { success: false, error: 'Dev mode not enabled' };
    }

    setIsSyncing(true);
    setError(null);

    try {
      const result = await inkquoteApi.triggerExport();

      if (!result.success) {
        setError(result.error);
      }

      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsSyncing(false);
    }
  }, [isDevMode]);

  // Start periodic connection checks
  const startPeriodicCheck = useCallback((intervalMs = 30000) => {
    if (!isDevMode) return;

    // Clear any existing interval
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    // Initial check
    checkConnection();

    // Set up periodic check
    checkIntervalRef.current = setInterval(() => {
      checkConnection();
    }, intervalMs);
  }, [isDevMode, checkConnection]);

  // Stop periodic checks
  const stopPeriodicCheck = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (isDevMode && inkquoteApi.isEnabled()) {
      checkConnection();
      // Start periodic checks every 30 seconds
      startPeriodicCheck(30000);
    }

    return () => {
      stopPeriodicCheck();
    };
  }, [isDevMode, checkConnection, startPeriodicCheck, stopPeriodicCheck]);

  // Listen for online/offline events
  useEffect(() => {
    if (!isDevMode) return;

    const handleOnline = () => {
      if (process.env.REACT_APP_MODE === 'development') {
        console.log('[ApiSync] Browser back online, checking connection...');
      }
      checkConnection();
    };

    const handleOffline = () => {
      if (process.env.REACT_APP_MODE === 'development') {
        console.log('[ApiSync] Browser offline');
      }
      setIsConnected(false);
      setError('Browser is offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isDevMode, checkConnection]);

  return {
    // State
    isEnabled: isDevMode && inkquoteApi.isEnabled(),
    isConnected,
    isChecking,
    isSyncing,
    lastCheckTime,
    queuedCount,
    error,

    // Methods
    checkConnection,
    syncNow,
    triggerExport,
    startPeriodicCheck,
    stopPeriodicCheck
  };
}

export default useApiSync;
